/**
 * HelpArticleResult — ligne de résultat d'article (recherche), avec surlignage gras
 * accent-insensible et badge « Sur cette page ». Extrait pour garder HelpSearchView
 * sous la limite de 300 lignes.
 */

import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'

import { cn } from '../../lib/utils'
import { highlightSegments } from '../../lib/helpSearch'

export function HelpArticleResult({
    title,
    query,
    onThisPage,
    active,
    onOpen,
    onHover,
    buttonRef,
}: {
    title: string
    query: string
    onThisPage: boolean
    active: boolean
    onOpen: () => void
    onHover: () => void
    buttonRef: (el: HTMLButtonElement | null) => void
}) {
    const { t } = useTranslation()
    const segments = highlightSegments(title, query)
    return (
        <button
            type="button"
            ref={buttonRef}
            onClick={onOpen}
            onMouseEnter={onHover}
            className={cn(
                'flex w-full items-center justify-between gap-3 rounded-xl bg-card px-4 py-3 text-left shadow-md',
                'transition-all duration-200 hover:shadow-lg motion-reduce:transition-none',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                active && 'ring-2 ring-primary',
            )}
        >
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">
                    {segments.map((s, i) =>
                        s.bold ? (
                            <strong key={i} className="font-bold text-foreground">
                                {s.text}
                            </strong>
                        ) : (
                            <Fragment key={i}>{s.text}</Fragment>
                        ),
                    )}
                </span>
                {onThisPage && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-accent px-2 py-0.5 text-[10px] font-medium text-primary">
                        {t('help.search.onThisPage')}
                    </span>
                )}
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
        </button>
    )
}
